import React, { useEffect, useState, useMemo } from "react";
import { enqueueSnackbar } from "notistack";
import DashboardLayout from "../../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { apostilasService, Apostila } from "../../services/apostilasService";
import {
  BookOpen,
  FolderOpen,
  ChevronRight,
  Search,
  X,
  Lock,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { InputText } from "../../components/InputText";

interface CategoriaGrupo {
  categoriaId: string | null;
  nome: string;
  apostilas: Apostila[];
}

export function ApostilasPainel() {
  const [apostilas, setApostilas] = useState<Apostila[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState("");
  const [apostilaSelecionada, setApostilaSelecionada] = useState<Apostila | null>(null);

  useEffect(() => {
    loadApostilas();
  }, []);

  async function loadApostilas() {
    try {
      setLoading(true);
      const data = await apostilasService.listPainel();
      setApostilas(data);
    } catch {
      enqueueSnackbar("Erro ao carregar apostilas", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  const apostilasFiltradas = useMemo(() => {
    if (!busca.trim()) return apostilas;
    const termo = busca.toLowerCase();
    return apostilas.filter(
      (a) =>
        a.titulo.toLowerCase().includes(termo) ||
        (a.descricao && a.descricao.toLowerCase().includes(termo)) ||
        (a.categoria?.nome.toLowerCase().includes(termo))
    );
  }, [apostilas, busca]);

  const grupos = useMemo((): CategoriaGrupo[] => {
    const mapa = new Map<string | null, { nome: string; apostilas: Apostila[]; ordem: number }>();

    for (const apostila of apostilasFiltradas) {
      const catId = apostila.categoria_id;
      if (!mapa.has(catId)) {
        mapa.set(catId, {
          nome: apostila.categoria?.nome || "Sem categoria",
          apostilas: [],
          ordem: apostila.categoria?.ordem ?? 9999,
        });
      }
      mapa.get(catId)!.apostilas.push(apostila);
    }

    return Array.from(mapa.entries())
      .map(([categoriaId, info]) => ({
        categoriaId,
        nome: info.nome,
        apostilas: info.apostilas.sort((a, b) => a.ordem - b.ordem),
        ordem: info.ordem,
      }))
      .sort((a, b) => (a as any).ordem - (b as any).ordem);
  }, [apostilasFiltradas]);

  if (apostilaSelecionada) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setApostilaSelecionada(null)}
              className="flex items-center gap-1 text-muted-foreground"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Voltar
            </Button>
            {apostilaSelecionada.categoria && (
              <span className="text-sm text-muted-foreground">
                {apostilaSelecionada.categoria.nome}
              </span>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{apostilaSelecionada.titulo}</CardTitle>
              {apostilaSelecionada.descricao && (
                <p className="text-sm text-muted-foreground mt-1">{apostilaSelecionada.descricao}</p>
              )}
            </CardHeader>
            <CardContent>
              <div
                className="prose prose-sm dark:prose-invert max-w-none [&_img]:max-w-full [&_img]:h-auto [&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:ml-0"
                dangerouslySetInnerHTML={{ __html: apostilaSelecionada.conteudo_html }}
              />
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Apostilas</h1>
            <p className="text-sm text-muted-foreground">
              Material de estudo e referência disponível para sua patente
            </p>
          </div>
        </div>

        {/* Busca */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <InputText
            placeholder="Buscar apostilas..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9 pr-8"
          />
          {busca && (
            <button
              onClick={() => setBusca("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : grupos.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
              <Lock className="h-10 w-10 opacity-30" />
              <p className="font-medium">Nenhuma apostila disponível</p>
              <p className="text-sm text-center max-w-xs">
                {busca
                  ? "Nenhuma apostila encontrada para essa busca."
                  : "Nenhuma apostila está disponível para sua patente no momento."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {grupos.map((grupo) => (
              <div key={grupo.categoriaId ?? "sem-categoria"}>
                {/* Header da categoria */}
                <div className="flex items-center gap-2 mb-4">
                  <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-base font-semibold text-foreground">{grupo.nome}</span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    {grupo.apostilas.length}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Cards das apostilas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grupo.apostilas.map((apostila) => (
                    <button
                      key={apostila.id}
                      onClick={() => setApostilaSelecionada(apostila)}
                      className="text-left rounded-lg border border-border bg-card p-4 hover:bg-accent/30 hover:border-primary/30 transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <BookOpen className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                            {apostila.titulo}
                          </p>
                          {apostila.descricao && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {apostila.descricao}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 group-hover:text-primary transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
